import { useEffect, useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { useSession } from '../../lib/store';
import { Card, Empty, Notice, PageHeading, Spinner } from '../../components/Shell';
import { Stamp } from '../../components/Stamp';
import { SelectField, TextArea, TextField, Toggle } from '../../components/Field';
import { money, minutesToTime, rateSuffix } from '../../lib/format';
import type { ServiceSummary } from '../../lib/types';

interface FullService extends ServiceSummary {
  specialNeedsSurcharge: number;
  offHoursSurcharge?: number;
  offHoursBeforeMinutes?: number;
  offHoursAfterMinutes?: number;
  requiresSignedAgreement: boolean;
}

/**
 * The rate card. Prices are edited in dollars and stored in cents, so a sitter
 * never has to think about the unit the database uses.
 */
export function AdminServices() {
  const session = useSession();
  const tenantId = session.tenant?.tenantId;

  const [services, setServices] = useState<FullService[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  // Delete is two-step and the confirm lives inline rather than in a browser
  // dialog, so the name of the thing being deleted stays on screen next to it.
  const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const load = () => {
    if (!tenantId) return Promise.resolve();
    return api<{ services: FullService[] }>(`/tenants/${tenantId}/services`)
      .then((result) => setServices(result.services))
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Services could not be loaded.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl px-5 py-16">
        <Spinner label="Loading your rates" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-5 pb-24 pt-12">
      <PageHeading
        eyebrow={session.tenant?.businessName}
        title="Services and rates"
        lead="What clients see, and what they are quoted. Changes apply to new bookings only; anything already quoted keeps its price."
        actions={
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              setAdding((open) => !open);
              setEditingId(null);
            }}
            aria-expanded={adding}
          >
            {adding ? 'Cancel' : 'Add a service'}
          </button>
        }
      />

      {saved && (
        <div className="mb-8">
          <Notice tone="ok">{saved}</Notice>
        </div>
      )}
      {error && (
        <div className="mb-8">
          <Notice tone="error">{error}</Notice>
        </div>
      )}

      {adding && tenantId && (
        <div className="mb-8">
          <NewServiceForm
            tenantId={tenantId}
            onCreated={async (name, serviceId) => {
              setSaved(`${name} added. Fill in the rest of its rates below.`);
              setAdding(false);
              await load();
              // Straight into the full editor: the create form asks only for
              // what a service cannot exist without, and everything else --
              // holiday rates, add-ons, the assessment rule -- is one screen
              // down. Opening it is the difference between "added" and "ready".
              setEditingId(serviceId);
            }}
            onError={setError}
          />
        </div>
      )}

      {services.length === 0 && !adding && (
        <Empty
          title="No services yet"
          body="Add the things you offer and what you charge for them. They appear on your booking page in the order you add them."
          action={{ label: 'Add a service', onClick: () => setAdding(true) }}
        />
      )}

      <ul className="space-y-4">
        {services.map((service) => (
          <li key={service.serviceId}>
            <Card className="p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="font-display text-xl font-bold">{service.name}</h2>
                  <p className="typed text-sm text-[color:var(--color-ink-soft)]">
                    {service.rateType === 'quote'
                      ? 'Quoted per trip'
                      : service.basePrice === 0
                        ? 'Free'
                        : `${money(service.basePrice)} ${rateSuffix(service.rateType)}`}
                    {service.additionalPetPrice > 0 &&
                      `, ${money(service.additionalPetPrice)} each extra pet`}
                  </p>
                  {service.windowStartMinutes !== undefined && service.windowEndMinutes !== undefined && (
                    <p className="font-mono text-xs text-[color:var(--color-ink-faint)]">
                      {minutesToTime(service.windowStartMinutes)} to{' '}
                      {minutesToTime(service.windowEndMinutes)}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {service.active === false && (
                    <Stamp tone="quiet" flat>
                      Hidden
                    </Stamp>
                  )}
                  <button
                    type="button"
                    className="btn btn-quiet"
                    onClick={() => {
                      setEditingId(editingId === service.serviceId ? null : service.serviceId);
                      setConfirmingDelete(null);
                    }}
                    aria-expanded={editingId === service.serviceId}
                  >
                    {editingId === service.serviceId ? 'Close' : 'Edit'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-quiet"
                    onClick={() => {
                      setConfirmingDelete(
                        confirmingDelete === service.serviceId ? null : service.serviceId
                      );
                      setError(null);
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>

              {confirmingDelete === service.serviceId && tenantId && (
                <DeleteConfirm
                  tenantId={tenantId}
                  service={service}
                  onCancel={() => setConfirmingDelete(null)}
                  onDeleted={async () => {
                    setSaved(`${service.name} deleted.`);
                    setConfirmingDelete(null);
                    await load();
                  }}
                  onHidden={async () => {
                    setSaved(`${service.name} is hidden. It is off your booking page and its old bookings still work.`);
                    setConfirmingDelete(null);
                    await load();
                  }}
                />
              )}

              {editingId === service.serviceId && tenantId && (
                <ServiceEditor
                  tenantId={tenantId}
                  service={service}
                  onSaved={async (name) => {
                    setSaved(`${name} updated.`);
                    setEditingId(null);
                    await load();
                  }}
                  onError={setError}
                />
              )}
            </Card>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ServiceEditor({
  tenantId,
  service,
  onSaved,
  onError,
}: {
  tenantId: string;
  service: FullService;
  onSaved: (name: string) => Promise<void>;
  onError: (message: string) => void;
}) {
  const dollars = (cents: number) => (cents / 100).toFixed(2);
  const cents = (value: string) => Math.max(0, Math.round(Number(value) * 100) || 0);

  const [name, setName] = useState(service.name);
  const [description, setDescription] = useState(service.description);
  const [basePrice, setBasePrice] = useState(dollars(service.basePrice));
  const [additionalPetPrice, setAdditionalPetPrice] = useState(dollars(service.additionalPetPrice));
  const [petsIncluded, setPetsIncluded] = useState(String(service.petsIncluded));
  const [holidaySurcharge, setHolidaySurcharge] = useState(dollars(service.holidaySurcharge));
  const [specialNeedsSurcharge, setSpecialNeedsSurcharge] = useState(
    dollars(service.specialNeedsSurcharge ?? 0)
  );
  const [offHoursSurcharge, setOffHoursSurcharge] = useState(dollars(service.offHoursSurcharge ?? 0));
  const [active, setActive] = useState(service.active !== false);
  const [requiresAssessment, setRequiresAssessment] = useState(service.requiresAssessment);
  const [busy, setBusy] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setFieldErrors({});

    void api(`/tenants/${tenantId}/services/${service.serviceId}`, {
      method: 'PUT',
      body: {
        name,
        description,
        code: service.code,
        rateType: service.rateType,
        basePrice: cents(basePrice),
        additionalPetPrice: cents(additionalPetPrice),
        petsIncluded: Math.max(1, Number(petsIncluded) || 1),
        holidaySurcharge: cents(holidaySurcharge),
        specialNeedsSurcharge: cents(specialNeedsSurcharge),
        offHoursSurcharge: cents(offHoursSurcharge),
        offHoursBeforeMinutes: service.offHoursBeforeMinutes,
        offHoursAfterMinutes: service.offHoursAfterMinutes,
        windowStartMinutes: service.windowStartMinutes,
        windowEndMinutes: service.windowEndMinutes,
        durationMinutes: service.durationMinutes,
        requiresAssessment,
        requiresSignedAgreement: service.requiresSignedAgreement,
        addOns: service.addOns,
        active,
        sortOrder: service.sortOrder,
      },
    })
      .then(() => onSaved(name))
      .catch((err) => {
        if (err instanceof ApiError && err.fieldErrors) setFieldErrors(err.fieldErrors);
        else onError(err instanceof ApiError ? err.message : 'That did not save.');
      })
      .finally(() => setBusy(false));
  };

  return (
    <form onSubmit={submit} className="mt-5 space-y-5 border-t border-[color:var(--color-rule)] pt-5">
      <TextField label="Name" value={name} onChange={setName} error={fieldErrors.name} required />
      <TextArea label="What it includes" value={description} onChange={setDescription} rows={3} />

      <div className="grid gap-5 sm:grid-cols-2">
        <TextField
          label={`Base price ${rateSuffix(service.rateType)}`}
          type="number"
          value={basePrice}
          onChange={setBasePrice}
          error={fieldErrors.basePrice}
        />
        <TextField
          label="Each extra pet"
          type="number"
          value={additionalPetPrice}
          onChange={setAdditionalPetPrice}
          error={fieldErrors.additionalPetPrice}
        />
        <TextField
          label="Pets covered by the base price"
          type="number"
          value={petsIncluded}
          onChange={setPetsIncluded}
          error={fieldErrors.petsIncluded}
        />
        <TextField
          label="Holiday rate, per pet per day"
          type="number"
          value={holidaySurcharge}
          onChange={setHolidaySurcharge}
        />
        <TextField
          label="Special care, per pet per day"
          type="number"
          value={specialNeedsSurcharge}
          onChange={setSpecialNeedsSurcharge}
          hint="For pets who are not potty trained, are escape artists, or need close medical care."
        />
        <TextField
          label="Off-hours fee"
          type="number"
          value={offHoursSurcharge}
          onChange={setOffHoursSurcharge}
          hint="Applied to drop-offs and pickups outside your normal window."
        />
      </div>

      <div className="space-y-1">
        <Toggle label="Show this on my booking page" checked={active} onChange={setActive} />
        <Toggle
          label="Requires a temperament assessment first"
          checked={requiresAssessment}
          onChange={setRequiresAssessment}
        />
      </div>

      <button type="submit" className="btn btn-primary" disabled={busy}>
        {busy ? 'Saving' : 'Save this service'}
      </button>
    </form>
  );
}

/**
 * The two-step confirm, and the recovery when the delete is refused.
 *
 * A service that has been booked cannot be removed without breaking those
 * bookings, and the API says so with a 409. That is not a dead end: hiding it
 * does what the sitter actually wanted, which is for it to stop appearing on
 * her booking page. So the refusal turns into the offer, in place, rather than
 * an error she has to work out what to do about.
 */
function DeleteConfirm({
  tenantId,
  service,
  onCancel,
  onDeleted,
  onHidden,
}: {
  tenantId: string;
  service: FullService;
  onCancel: () => void;
  onDeleted: () => Promise<void>;
  onHidden: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [inUse, setInUse] = useState<string | null>(null);

  const remove = () => {
    setBusy(true);
    void api(`/tenants/${tenantId}/services/${service.serviceId}`, { method: 'DELETE' })
      .then(onDeleted)
      .catch((err) => {
        if (err instanceof ApiError && err.status === 409) setInUse(err.message);
        else setInUse(err instanceof ApiError ? err.message : 'That could not be deleted.');
      })
      .finally(() => setBusy(false));
  };

  const hide = () => {
    setBusy(true);
    void api(`/tenants/${tenantId}/services/${service.serviceId}`, {
      method: 'PUT',
      body: { name: service.name, code: service.code, rateType: service.rateType, active: false },
    })
      .then(onHidden)
      .catch((err) => setInUse(err instanceof ApiError ? err.message : 'That could not be hidden.'))
      .finally(() => setBusy(false));
  };

  return (
    <div className="mt-4 border-t border-[color:var(--color-rule)] pt-4">
      {inUse ? (
        <>
          <p className="text-sm">{inUse}</p>
          <div className="mt-3 flex flex-wrap gap-3">
            {service.active !== false && (
              <button type="button" className="btn btn-secondary" onClick={hide} disabled={busy}>
                {busy ? 'Hiding' : 'Hide it instead'}
              </button>
            )}
            <button type="button" className="btn btn-quiet" onClick={onCancel} disabled={busy}>
              Leave it alone
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="text-sm">
            Delete {service.name}? This cannot be undone. If it has ever been booked, it will be
            kept and hidden instead so those bookings keep working.
          </p>
          <div className="mt-3 flex flex-wrap gap-3">
            <button type="button" className="btn btn-danger" onClick={remove} disabled={busy}>
              {busy ? 'Deleting' : 'Delete it'}
            </button>
            <button type="button" className="btn btn-quiet" onClick={onCancel} disabled={busy}>
              Keep it
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/** The kinds of service the pricing engine knows how to quote. */
const SERVICE_CODES: { value: string; label: string }[] = [
  { value: 'boarding', label: 'Overnight boarding' },
  { value: 'day_care', label: 'Doggie day care' },
  { value: 'pop_in', label: 'Pop-in visit' },
  { value: 'house_sitting', label: 'House sitting' },
  { value: 'dog_walk', label: 'Dog walk' },
  { value: 'pet_taxi', label: 'Pet transport' },
  { value: 'meet_greet', label: 'Meet and greet' },
  { value: 'assessment', label: 'Temperament assessment' },
  { value: 'pup_date', label: 'Pup date' },
];

const RATE_TYPES: { value: string; label: string }[] = [
  { value: 'per_night', label: 'A night' },
  { value: 'per_day', label: 'A day' },
  { value: 'per_visit', label: 'A visit' },
  { value: 'per_hour', label: 'An hour' },
  { value: 'flat', label: 'One flat price' },
  { value: 'quote', label: 'Quoted per trip' },
];

/**
 * Adding a service asks for the four things it cannot exist without and
 * nothing else. The full editor opens on it immediately afterwards, so the
 * long tail of holiday rates and add-ons is one screen away rather than
 * standing between a sitter and a service that exists.
 *
 * `code` is fixed at creation because the pricing engine branches on it and
 * saveService will not let it change later. That is why it is a choice here
 * and absent from the editor below.
 */
function NewServiceForm({
  tenantId,
  onCreated,
  onError,
}: {
  tenantId: string;
  onCreated: (name: string, serviceId: string) => Promise<void>;
  onError: (message: string) => void;
}) {
  const [name, setName] = useState('');
  const [code, setCode] = useState('boarding');
  const [rateType, setRateType] = useState('per_night');
  const [basePrice, setBasePrice] = useState('0.00');
  const [busy, setBusy] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setFieldErrors({});

    void api<{ service: FullService }>(`/tenants/${tenantId}/services`, {
      method: 'POST',
      body: {
        name,
        code,
        rateType,
        // A quoted service has no base price to set, and sending one would
        // put a number on a rate card that is meant to say "ask me".
        basePrice: rateType === 'quote' ? 0 : Math.max(0, Math.round(Number(basePrice) * 100) || 0),
        description: '',
      },
    })
      .then((result) => onCreated(result.service.name, result.service.serviceId))
      .catch((err) => {
        if (err instanceof ApiError && err.fieldErrors) setFieldErrors(err.fieldErrors);
        else onError(err instanceof ApiError ? err.message : 'That service could not be added.');
      })
      .finally(() => setBusy(false));
  };

  return (
    <Card tint="yellow" className="p-5">
      <h2 className="font-display text-xl font-bold">A new service</h2>
      <form onSubmit={submit} className="mt-4 space-y-5">
        <TextField
          label="What do you call it?"
          value={name}
          onChange={setName}
          error={fieldErrors.name}
          placeholder="Private overnight boarding"
          required
        />

        <div className="grid gap-5 sm:grid-cols-2">
          <SelectField
            label="What kind of service is it?"
            value={code}
            onChange={setCode}
            options={SERVICE_CODES}
            error={fieldErrors.code}
          />
          <SelectField
            label="How do you charge for it?"
            value={rateType}
            onChange={setRateType}
            options={RATE_TYPES}
            error={fieldErrors.rateType}
          />
        </div>

        {rateType !== 'quote' && (
          <div className="sm:max-w-xs">
            <TextField
              label={`Base price ${rateSuffix(rateType)}`.trim()}
              type="number"
              value={basePrice}
              onChange={setBasePrice}
              error={fieldErrors.basePrice}
              hint="Leave it at zero if this one is free. You can change it any time."
            />
          </div>
        )}

        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? 'Adding' : 'Add this service'}
        </button>
      </form>
    </Card>
  );
}
